import React, { useMemo, useState } from 'react';
import Popover from '../Popover';
import Icon from '../Icon';
import { ICONS } from '../icon-map';
import { useModal } from '../../contexts/ModalContext';
import useEjarStatus from '../../hooks/useEjarStatus';

const SEEN_KEY = 'yelo.notifications.seenAt';

const readSeen = () => {
    try { return Number(localStorage.getItem(SEEN_KEY)) || 0; } catch (e) { return 0; }
};

const relTime = (ms) => {
    const s = Math.round((Date.now() - ms) / 1000);
    if (s < 60) return 'just now';
    const m = Math.round(s / 60);
    if (m < 60) return `${m} min ago`;
    const h = Math.round(m / 60);
    if (h < 24) return `${h} h ago`;
    return new Date(ms).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
};

const TYPE_ICON = { success: ICONS.success, error: ICONS.error, warning: ICONS.warning, info: ICONS.info };

/** Bell in the top bar: integration health plus the recent activity messages of this session. */
export default function NotificationCenter() {
    const { feedbackLog = [], clearFeedbackLog } = useModal();
    const { status: ejar, refresh } = useEjarStatus();
    const [seenAt, setSeenAt] = useState(readSeen);

    const ejarItem = useMemo(() => {
        if (!ejar) return null;
        const when = ejar.last_run ? new Date(ejar.last_run) : null;
        if (ejar.last_error) {
            return { type: 'error', title: 'Ejar sync failed', text: String(ejar.last_error).slice(0, 140), at: when ? when.getTime() : Date.now() };
        }
        return { type: 'success', title: 'Ejar sync healthy', text: when ? `Last run ${when.toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}` : 'Waiting for the first run', at: when ? when.getTime() : 0 };
    }, [ejar]);

    const unread = feedbackLog.filter((l) => l.at > seenAt && (l.type === 'error' || l.type === 'warning')).length
        + (ejarItem && ejarItem.type === 'error' ? 1 : 0);

    const markSeen = () => {
        const now = Date.now();
        setSeenAt(now);
        try { localStorage.setItem(SEEN_KEY, String(now)); } catch (e) { /* ignore */ }
    };

    return (
        <Popover
            label="Notifications"
            width={340}
            onOpenChange={(open) => { if (open) markSeen(); }}
            trigger={({ props }) => (
                <button {...props} type="button" className="shell-iconbtn" aria-label={unread ? `Notifications, ${unread} need attention` : 'Notifications'}>
                    <Icon glyph={ICONS.bell} size="sm" />
                    {unread > 0 && <span className="shell-iconbtn__pip" aria-hidden="true" />}
                </button>
            )}
        >
            <div className="notif">
                <div className="ds-menu-heading">Integrations</div>
                {ejarItem ? (
                    <div className={`notif__item notif__item--${ejarItem.type}`}>
                        <Icon glyph={TYPE_ICON[ejarItem.type]} size="sm" />
                        <div className="notif__body">
                            <div className="notif__title">{ejarItem.title}</div>
                            <div className="notif__text">{ejarItem.text}</div>
                        </div>
                        <button type="button" className="yelo-btn yelo-btn--ghost yelo-btn--xs" onClick={refresh}>Check</button>
                    </div>
                ) : (
                    <div className="notif__empty">Checking integration status…</div>
                )}
                <div className="ds-menu-divider" />
                <div className="notif__head">
                    <div className="ds-menu-heading">Recent activity</div>
                    {feedbackLog.length > 0 && (
                        <button type="button" className="yelo-btn yelo-btn--link yelo-btn--xs" onClick={clearFeedbackLog}>Clear</button>
                    )}
                </div>
                {feedbackLog.length === 0 ? (
                    <div className="notif__empty">Messages from this session appear here.</div>
                ) : (
                    feedbackLog.slice(0, 12).map((l) => (
                        <div key={l.id} className={`notif__item notif__item--${l.type}`}>
                            <Icon glyph={TYPE_ICON[l.type] || ICONS.info} size="sm" />
                            <div className="notif__body">
                                <div className="notif__text notif__text--main" dir="auto">{l.message}</div>
                                <div className="notif__time">{relTime(l.at)}</div>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </Popover>
    );
}
